import React, { useState, useCallback } from 'react';
import T from 'prop-types';
import { times, uniq, map } from 'lodash-es';

import MarketApp from './MarketApp';
import MarketAppPlaceholder from './MarketAppPlaceholder';
import SlideTopModal from '../../../shared/components/SlideTopModal';
import FindAppModalContent from '../../../shared/components/FindAppModalContent';
import Button from '../../../../../../components/Buttons/Button';
import useMobileView from '../../../shared/hooks/useMobileView';

import { getReportCharts } from '../../api';

const maxAppsCount = 5;
const minSelectedApps = 3;

const isAppsChanged = (prevApps, nextApps) => {
  if (prevApps.length !== nextApps.length) return true;

  return uniq([...map(prevApps, 'ref_no'), ...map(nextApps, 'ref_no')]).length !== prevApps.length;
};

const EditingMarketApps = ({
  selectedApps,
  popularApps,
  withChartsRequest = false,
  onClose,
  onChange,
  onChartsChange,
}) => {
  const [apps, setApps] = useState(selectedApps);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [showAddAppModal, setShowAddAppModal] = useState(false);
  const isMobileView = useMobileView();

  const isUpdateDisabled = loading || apps.length < minSelectedApps;

  const handleMarketAppsUpdate = () => {
    if (isUpdateDisabled) return;

    if (withChartsRequest && isAppsChanged(selectedApps, apps)) {
      const requestData = { apps: apps.map(({ ref_no, store }) => ({ ref_no, store })) };

      setLoading(true);
      setServerError(false);
      getReportCharts(requestData)
        .then(({ data }) => {
          setLoading(false);

          if (onChartsChange) onChartsChange(data);
          onChange(apps);
          onClose(false);
        })
        .catch(() => {
          setLoading(false);
          setServerError(true);
        });
    } else {
      onChange(apps);
      onClose(false);
    }
  };

  const handleRemoveApp = useCallback(({ ref_no }) => {
    setApps((prevState) => prevState.filter((app) => app.ref_no !== ref_no));
  }, []);

  const showCancelButton = !loading && isAppsChanged(selectedApps, apps);

  return (
    <div className="editing-market-apps">
      <h2 className="editing-market-apps__title">Edit Apps</h2>
      {apps.length < minSelectedApps && (
        <p className="editing-market-apps__warning">
          Please select at least {minSelectedApps} apps for your report
        </p>
      )}
      {serverError && (
        <p className="editing-market-apps__warning">
          Oops! Something went wrong. Please, try again later!
        </p>
      )}
      <div className="editing-market-apps__body">
        {apps.map((app) => (
          <MarketApp
            key={app.ref_no}
            app={app}
            isMobileView={isMobileView}
            onRemoveApp={handleRemoveApp}
          />
        ))}
        {times(maxAppsCount - apps.length, (index) => (
          <MarketAppPlaceholder
            key={index}
            isMobileView={isMobileView}
            onAddApp={setShowAddAppModal}
          />
        ))}
      </div>
      <div className="editing-market-apps__footer">
        <Button
          size="lg"
          loading={loading}
          disabled={isUpdateDisabled}
          onClick={handleMarketAppsUpdate}
          loadingId="editing-market-apps__footer-loading"
        >
          Looks Good!
        </Button>
        {showCancelButton && (
          <span className="btn-underline btn-underline--secondary" onClick={() => onClose(false)}>
            Cancel
          </span>
        )}
      </div>

      <SlideTopModal
        show={isMobileView && showAddAppModal}
        modalTitle="Add an App"
        className="find-app-modal"
        onHide={setShowAddAppModal}
      >
        <FindAppModalContent
          selectedApps={apps}
          popularApps={popularApps}
          onChange={setApps}
          onHide={setShowAddAppModal}
        />
      </SlideTopModal>

      {!isMobileView && showAddAppModal && (
        <div className="find-app-modal find-app-modal-desktop">
          <header className="find-app-modal__header">
            <span className="hicon-angle-right-alt" onClick={() => setShowAddAppModal(false)} />
            <p className="find-app-modal__header-title">Select an App</p>
          </header>
          <div className="find-app-modal__body">
            <FindAppModalContent
              selectedApps={apps}
              popularApps={popularApps}
              onChange={setApps}
              onHide={setShowAddAppModal}
            />
          </div>
        </div>
      )}
    </div>
  );
};

EditingMarketApps.propTypes = {
  selectedApps: T.array.isRequired,
  popularApps: T.array.isRequired,
  withChartsRequest: T.bool,
  onClose: T.func.isRequired,
  onChange: T.func.isRequired,
  onChartsChange: T.func,
};

export default EditingMarketApps;
